import { createServerFn } from "@tanstack/react-start";
import crypto from "node:crypto";

import { requireSupabaseAuth } from "@/lib/require-auth";
import { bokunFetch } from "./bokun.server";

async function ensureAdmin(userId: string, supabase: any) {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .in("role", ["admin", "owner"])
    .limit(1);
  if (error) throw new Error(error.message);
  if (!data || data.length === 0) throw new Error("No tienes permisos de administrador.");
}

export interface BokunStatus {
  apiFlavor: "REST Legacy v1 (HMAC-SHA1 signed)";
  baseUrl: string;
  productsEndpoint: string;
  accessKeyConfigured: boolean;
  secretKeyConfigured: boolean;
  accessKeyLength: number;
  ok: boolean;
  httpStatus: number | null;
  httpStatusText: string | null;
  productCount: number | null;
  lastSyncIso: string;
  elapsedMs: number;
  sampleRaw: string | null; // first 800 chars of raw response
  sampleParsed: string | null; // first item (truncated JSON string) if available
  authError: string | null;
  hint: string | null;
}

export const getBokunStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<BokunStatus> => {
    await ensureAdmin(context.userId, context.supabase);
  const accessKey = process.env.BOKUN_ACCESS_KEY ?? "";
  const secretKey = process.env.BOKUN_SECRET_KEY ?? "";
  const base = (process.env.BOKUN_API_URL || "https://api.bokun.io").replace(/\/+$/, "");
  const path = "/activity.json/search";
  const productsEndpoint = `${base}${path}`;
  const startedAt = Date.now();

  const status: BokunStatus = {
    apiFlavor: "REST Legacy v1 (HMAC-SHA1 signed)",
    baseUrl: base,
    productsEndpoint,
    accessKeyConfigured: !!accessKey,
    secretKeyConfigured: !!secretKey,
    accessKeyLength: accessKey.length,
    ok: false,
    httpStatus: null,
    httpStatusText: null,
    productCount: null,
    lastSyncIso: new Date().toISOString(),
    elapsedMs: 0,
    sampleRaw: null,
    sampleParsed: null,
    authError: null,
    hint: null,
  };

  if (!accessKey || !secretKey) {
    status.authError = "BOKUN_ACCESS_KEY or BOKUN_SECRET_KEY missing.";
    status.hint = "Set both secrets in Project Settings → Secrets.";
    status.elapsedMs = Date.now() - startedAt;
    return status;
  }

  // Sign the request inline so we can capture the raw HTTP response.
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const date = `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())}`;
  const method = "POST";
  const signature = crypto.createHmac("sha1", secretKey).update(date + accessKey + method + path).digest("base64");

  try {
    const res = await fetch(productsEndpoint, {
      method,
      headers: {
        "X-Bokun-Date": date,
        "X-Bokun-AccessKey": accessKey,
        "X-Bokun-Signature": signature,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ forSale: true, pageSize: 5, page: 1 }),
    });
    status.httpStatus = res.status;
    status.httpStatusText = res.statusText;
    const text = await res.text();
    status.sampleRaw = text.slice(0, 800);

    if (res.status === 401 || res.status === 403) {
      status.authError = `Authentication rejected (${res.status}). The access key may be invalid, the signature wrong, or the scope LEGACY_API not enabled on this key.`;
      status.hint = "In Bokun → Settings → API → confirm the access key has the LEGACY_API scope enabled.";
    } else if (res.status === 404) {
      status.authError = `Endpoint not found (404) at ${productsEndpoint}.`;
      status.hint = "BOKUN_API_URL likely points to a mock/sandbox host. Set it to https://api.bokun.io (production) for the REST Legacy v1 API.";
    } else if (!res.ok) {
      status.authError = `HTTP ${res.status} ${res.statusText}`;
    }

    if (res.ok) {
      try {
        const json = JSON.parse(text);
        const items = Array.isArray(json)
          ? json
          : Array.isArray(json?.items)
            ? json.items
            : Array.isArray(json?.results)
              ? json.results
              : [];
        status.productCount = items.length;
        status.ok = true;
        if (items[0]) {
          status.sampleParsed = JSON.stringify(items[0], null, 2).slice(0, 1500);
        }
      } catch (e) {
        status.authError = `Response was not JSON: ${(e as Error).message}`;
      }
    }
  } catch (err) {
    status.authError = `Network error: ${(err as Error).message}`;
  }

  status.elapsedMs = Date.now() - startedAt;
  return status;
});

// Keep bokunFetch referenced so tree-shaking does not remove it from server bundle traces.
void bokunFetch;
